import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Button, Box, Typography, Paper, TextField,
    IconButton, InputAdornment, CircularProgress, Alert
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import PremiumToast from '../components/PremiumToast';
import { useToast } from '../context/ToastContext';
import PasswordStrength from '../components/PasswordStrength';

const ForgotPassword = ({ role }) => {
    const navigate = useNavigate();

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(false);

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordErrors, setPasswordErrors] = useState([]);

    const [loader, setLoader] = useState(false);
    const { showToast } = useToast();

    const getEndpoint = (action) => {
        const baseUrl = process.env.REACT_APP_BASE_URL;
        return `${baseUrl}/${role}${action}`;
    };

    const validatePassword = (password) => {
        const errors = [];

        if (password.length < 8) {
            errors.push('Password must be at least 8 characters long');
        }
        if (!/[A-Z]/.test(password)) {
            errors.push('Password must contain at least one uppercase letter');
        }
        if (!/[a-z]/.test(password)) {
            errors.push('Password must contain at least one lowercase letter');
        }
        if (!/[0-9]/.test(password)) {
            errors.push('Password must contain at least one number');
        }
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
            errors.push('Password must contain at least one special character');
        }

        return errors;
    };

    const handleResetPassword = async () => {
        setPasswordErrors([]);
        let isValid = true;

        if (!email) {
            setEmailError(true);
            isValid = false;
        }

        if (newPassword !== confirmPassword) {
            showToast('Passwords do not match', 'error');
            return;
        }

        const errors = validatePassword(newPassword);
        if (errors.length > 0) {
            setPasswordErrors(errors);
            showToast('Password does not meet requirements', 'warning');
            return;
        }

        if (!isValid) return;

        setLoader(true);

        try {
            const fields = { email, newPassword, role };

            const result = await axios.post(getEndpoint('ForgotPassword'), fields, {
                headers: { 'Content-Type': 'application/json' },
            });

            if (result.data.message) {
                const isSuccess = result.data.message.toLowerCase().includes('successfully');
                showToast(result.data.message, isSuccess ? 'success' : 'info');
                if (isSuccess) {
                    setTimeout(() => {
                        navigate(`/${role}login`);
                    }, 2000);
                }
            }
        } catch (error) {
            showToast(error.response?.data?.message || 'Network Error. Please try again.', 'error');
        } finally {
            setLoader(false);
        }
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        
        if (name === 'email') {
            setEmail(value);
            setEmailError(false);
        }
        if (name === 'newPassword') {
            setNewPassword(value);
            setPasswordErrors([]);
        }
        if (name === 'confirmPassword') {
            setConfirmPassword(value);
        }
    };

    return (
        <ContainerWrapper>
            <Blob1 />
            <Blob2 />
            <GridPattern />

            <BackButton onClick={() => navigate(`/${role}login`)}>
                <ArrowBackIcon sx={{ mr: 1, fontSize: 20 }} />
                Back to Login
            </BackButton>

            <ContentWrapper>
                <GlassCard elevation={0}>
                    <TitleSection>
                        <RoleBadge>
                            {role} Recovery
                        </RoleBadge>
                        <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', mb: 1, letterSpacing: '-0.02em' }}>
                            Reset Password
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#64748b' }}>
                            Enter your email and a new secure password.
                        </Typography>
                    </TitleSection>

                    <Box component="form" noValidate sx={{ mt: 4, width: '100%' }}>
                        <StyledTextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Enter your email"
                            name="email"
                            autoComplete="email"
                            value={email}
                            error={emailError}
                            helperText={emailError && 'Email is required'}
                            onChange={handleInputChange}
                        />

                        {passwordErrors.length > 0 && (
                            <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    {passwordErrors.map((err, i) => (
                                        <li key={i}>{err}</li>
                                    ))}
                                </ul>
                            </Alert>
                        )}

                        <StyledTextField
                            margin="normal"
                            required
                            fullWidth
                            id="newPassword"
                            label="New Password"
                            name="newPassword"
                            type={showNewPassword ? 'text' : 'password'}
                            value={newPassword}
                            onChange={handleInputChange}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowNewPassword(!showNewPassword)}>
                                            {showNewPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        
                        <PasswordStrength password={newPassword} />

                        <StyledTextField
                            margin="normal"
                            required
                            fullWidth
                            id="confirmPassword"
                            label="Confirm Password"
                            name="confirmPassword"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            error={confirmPassword !== "" && newPassword !== "" && newPassword !== confirmPassword}
                            helperText={confirmPassword !== "" && newPassword !== "" && newPassword !== confirmPassword ? "Passwords do not match" : ""}
                            onChange={handleInputChange}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                            {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <PrimaryButton 
                            fullWidth 
                            variant="contained" 
                            sx={{ mt: 4 }}
                            onClick={handleResetPassword}
                            disabled={loader || !email || !newPassword || !confirmPassword}
                        >
                            {loader ? <CircularProgress size={24} sx={{ color: 'white' }} /> : "Reset Password"}
                        </PrimaryButton>

                        <OptionsRow>
                            <StyledLink to={`/${role}login`}>Cancel and return to login</StyledLink>
                        </OptionsRow>
                    </Box>
                </GlassCard>
            </ContentWrapper>
            
            <PremiumToast />
        </ContainerWrapper>
    );
};

export default ForgotPassword;

/* ===== ANIMATIONS & STYLED COMPONENTS ===== */

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
`;

const float1 = keyframes`
    0% { transform: translate(0, 0) rotate(0deg); }
    33% { transform: translate(30px, -50px) rotate(10deg); }
    66% { transform: translate(-20px, 20px) rotate(-5deg); }
    100% { transform: translate(0, 0) rotate(0deg); }
`;

const float2 = keyframes`
    0% { transform: translate(0, 0) rotate(0deg); }
    33% { transform: translate(-30px, 40px) rotate(-10deg); }
    66% { transform: translate(20px, -20px) rotate(5deg); }
    100% { transform: translate(0, 0) rotate(0deg); }
`;

const ContainerWrapper = styled.div`
    min-height: 100vh;
    width: 100%;
    background-color: #fafafa;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const GridPattern = styled.div`
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background-image: 
        linear-gradient(to right, rgba(99, 102, 241, 0.05) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(99, 102, 241, 0.05) 1px, transparent 1px);
    background-size: 40px 40px;
    z-index: 1;
    pointer-events: none;
    mask-image: radial-gradient(circle at center, black 40%, transparent 80%);
    -webkit-mask-image: radial-gradient(circle at center, black 40%, transparent 80%);
`;

const Blob1 = styled.div`
    position: absolute;
    top: -10%; left: -10%;
    width: 600px; height: 600px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, rgba(99, 102, 241, 0) 70%);
    border-radius: 50%;
    animation: ${float1} 15s ease-in-out infinite;
    z-index: 0;
    pointer-events: none;
`;

const Blob2 = styled.div`
    position: absolute;
    bottom: -20%; right: -10%;
    width: 800px; height: 800px;
    background: radial-gradient(circle, rgba(168, 85, 247, 0.1) 0%, rgba(168, 85, 247, 0) 70%);
    border-radius: 50%;
    animation: ${float2} 20s ease-in-out infinite;
    z-index: 0;
    pointer-events: none;
`;

const ContentWrapper = styled(Box)`
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 10;
    padding: 24px;
`;

const BackButton = styled(Box)`
    position: absolute;
    top: 40px;
    left: 40px;
    display: flex;
    align-items: center;
    color: #64748b;
    font-weight: 600;
    font-size: 0.95rem;
    cursor: pointer;
    z-index: 20;
    transition: all 0.2s ease;
    padding: 8px 16px;
    border-radius: 100px;
    background: rgba(255, 255, 255, 0.5);
    backdrop-filter: blur(10px);

    &:hover {
        color: #0f172a;
        background: white;
        box-shadow: 0 4px 12px rgba(0,0,0,0.05);
        transform: translateX(-4px);
    }

    @media (max-width: 600px) {
        top: 20px;
        left: 20px;
    }
`;

const GlassCard = styled(Paper)`
    && {
        width: 100%;
        max-width: 480px;
        padding: 48px;
        border-radius: 32px;
        background: rgba(255, 255, 255, 0.85);
        backdrop-filter: blur(20px);
        border: 1px solid rgba(255, 255, 255, 0.9);
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.08);
        animation: ${fadeIn} 0.6s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;

        @media (max-width: 600px) {
            padding: 32px 24px;
            border-radius: 24px;
        }
    }
`;

const TitleSection = styled(Box)`
    text-align: left;
    margin-bottom: 8px;
`;

const RoleBadge = styled(Box)`
    display: inline-block;
    padding: 6px 16px;
    background: #f1f5f9;
    color: #475569;
    border-radius: 100px;
    font-size: 0.85rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    margin-bottom: 24px;
    border: 1px solid #e2e8f0;
`;

const StyledTextField = styled(TextField)`
    & .MuiOutlinedInput-root {
        border-radius: 16px;
        background-color: white;
        transition: all 0.3s ease;
        
        fieldset {
            border-color: #e2e8f0;
            border-width: 1px;
            transition: all 0.2s ease;
        }
        
        &:hover fieldset {
            border-color: #cbd5e1;
        }
        
        &.Mui-focused {
            box-shadow: 0 4px 20px rgba(15, 23, 42, 0.05);
            
            fieldset {
                border-color: #0f172a !important;
                border-width: 2px;
            }
        }
    }
    
    & .MuiInputLabel-root {
        color: #64748b;
        font-weight: 500;
        
        &.Mui-focused {
            color: #0f172a;
        }
    }
`;

const OptionsRow = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 24px;
`;

const StyledLink = styled(Link)`
    text-decoration: none;
    color: #64748b;
    font-weight: 600;
    font-size: 0.95rem;
    transition: all 0.2s ease;
    
    &:hover {
        color: #0f172a;
        text-decoration: underline;
    }
`;

const PrimaryButton = styled(Button)`
    && {
        background: #0f172a;
        color: white;
        padding: 16px;
        border-radius: 16px;
        font-size: 1.1rem;
        font-weight: 700;
        text-transform: none;
        box-shadow: 0 10px 25px -5px rgba(15, 23, 42, 0.3);
        transition: all 0.3s ease;
        
        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 20px 35px -5px rgba(15, 23, 42, 0.4);
            background: #1e293b;
        }
        
        &:active {
            transform: translateY(0);
        }

        &.Mui-disabled {
            background: #cbd5e1;
            color: white;
            box-shadow: none;
        }
    }
`;
